import React from "react"
import Layout from "../components/Layout"
import { SEO } from "../components/SEO/SEO"
import { PageHero } from "../components/programmatic/PageHero"
import { FAQBlock } from "../components/programmatic/FAQBlock"
import { ServiceCTA } from "../components/programmatic/ServiceCTA"
import { RelatedLinks } from "../components/programmatic/RelatedLinks"
import { Breadcrumbs } from "../components/programmatic/Breadcrumbs"
import { AuthorBox } from "../components/programmatic/AuthorBox"
import { Markdown } from "../components/programmatic/Markdown"
import { buildLocalBusiness } from "../components/SEO/schema/localBusiness"
import { buildService } from "../components/SEO/schema/service"
import { buildFaqPage } from "../components/SEO/schema/faqPage"
import { buildBreadcrumbList } from "../components/SEO/schema/breadcrumbList"
import type { LocationPage } from "../data/types"
import { SERVICE_PATHS } from "../data/site"

type Props = {
  pageContext: LocationPage
}

const LocationTemplate = ({ pageContext }: Props) => {
  const path = `/${pageContext.slug}`
  const servicePath = SERVICE_PATHS[pageContext.service]
  const crumbs = [
    { name: "Início", to: "/" },
    { name: pageContext.serviceLabel, to: servicePath },
    {
      name: `${pageContext.serviceLabel} em ${pageContext.areaLabel}`,
      to: path,
    },
  ]

  const jsonLd = [
    buildLocalBusiness(),
    buildService({
      name: `${pageContext.serviceLabel} em ${pageContext.areaLabel}`,
      description: pageContext.metaDescription,
      url: path,
      serviceType: pageContext.serviceLabel,
      areaServed: [pageContext.areaLabel],
    }),
    buildFaqPage(pageContext.faq),
    buildBreadcrumbList(crumbs),
  ]

  return (
    <Layout>
      <SEO
        title={pageContext.metaTitle}
        description={pageContext.metaDescription}
        canonicalPath={path}
        jsonLd={jsonLd}
      />
      <Breadcrumbs crumbs={crumbs} />
      <PageHero
        eyebrow="Atendimento local"
        headline={`${pageContext.serviceLabel} em ${pageContext.areaLabel}${
          pageContext.isCityHub ? "" : ", Porto Alegre"
        }`}
        intro={pageContext.intro}
      />
      <section className="text-gray-700 body-font">
        <div className="container mx-auto px-5 py-10">
          <div className="max-w-3xl space-y-10">
            <article>
              <h2 className="title-font text-2xl md:text-3xl font-medium text-gray-900 mb-4">
                Como é o atendimento aqui
              </h2>
              <div className="text-base md:text-lg leading-relaxed text-gray-700">
                <Markdown source={pageContext.serviceMode} />
              </div>
            </article>
            {pageContext.transitNotes ? (
              <article>
                <h2 className="title-font text-2xl md:text-3xl font-medium text-gray-900 mb-4">
                  Como chegar
                </h2>
                <div className="text-base md:text-lg leading-relaxed text-gray-700">
                  <Markdown source={pageContext.transitNotes} />
                </div>
              </article>
            ) : null}
            {pageContext.parkingNotes ? (
              <article>
                <h2 className="title-font text-xl md:text-2xl font-medium text-gray-900 mb-3">
                  Estacionamento
                </h2>
                <div className="text-base md:text-lg leading-relaxed text-gray-700">
                  <Markdown source={pageContext.parkingNotes} />
                </div>
              </article>
            ) : null}
            {pageContext.neighborhoodNotes ? (
              <article>
                <h2 className="title-font text-2xl md:text-3xl font-medium text-gray-900 mb-4">
                  De onde vêm meus clientes
                </h2>
                <div className="text-base md:text-lg leading-relaxed text-gray-700">
                  <Markdown source={pageContext.neighborhoodNotes} />
                </div>
              </article>
            ) : null}
            {pageContext.testimonial ? (
              <blockquote className="border-l-4 border-purple-500 pl-5 py-2 italic text-gray-700">
                <p className="mb-2">"{pageContext.testimonial.text}"</p>
                <footer className="text-sm not-italic text-gray-500">
                  — {pageContext.testimonial.name}
                </footer>
              </blockquote>
            ) : null}
          </div>
        </div>
      </section>
      <ServiceCTA
        prefill={pageContext.ctaPrefill}
        headline={`Agendar ${pageContext.serviceLabel} em ${pageContext.areaLabel}`}
      />
      <FAQBlock faq={pageContext.faq} />
      <RelatedLinks links={pageContext.related} />
      <AuthorBox />
    </Layout>
  )
}

export default LocationTemplate
